import { useEffect } from "react";
import { getNotFoundStrings } from "../i18n/notFoundStrings";
import { SiteFooter } from "../components/SiteFooter";
import { SiteHeader } from "../components/SiteHeader";
import { usePageLanguage } from "../languages/usePageLanguage";
import { localizedPath } from "../utils/localizedPath";

// Shared by every "nothing here" surface (unmatched routes, unknown CMS slugs, unrecognized
// `:lang` segments). Without this, the page kept whatever <title>/canonical/robots the previous
// route (or the static index.html shell) had left behind — so a mistyped or guessed URL was a
// 200 OK, "index, follow" page that silently claimed to be the homepage while showing "404".
//
// The HTTP status is the Worker's job (web/worker.ts answers 404 for any path with no route in
// src/routes/spaRoutes.ts, with the same copy); this keeps the document honest for an in-app
// navigation, which never goes back to the Worker. HomePage sets its head tags without cleaning them up
// on unmount, so everything home-specific is removed here rather than assumed absent.
export function NotFoundPage() {
  // From the path, not useParams: "/some-invalid" reaches this page through the "/:lang" route with
  // lang="some-invalid", and "/zh-cn/some-invalid" through "*" with no lang at all. Only a real language
  // prefix is a language (the same rule as the edge's own 404 page), so those are the default language
  // and zh-cn respectively.
  const { lang, defaultUrlCode } = usePageLanguage();
  const strings = getNotFoundStrings(lang);
  const path = (target: string) => localizedPath(lang, defaultUrlCode, target);

  useEffect(() => {
    document.title = strings.documentTitle;
    setMeta("name", "description", strings.metaDescription);
    setMeta("name", "robots", "noindex, follow");
    setMeta("property", "og:title", strings.documentTitle);
    setMeta("property", "og:description", strings.metaDescription);
    setMeta("name", "twitter:title", strings.documentTitle);
    setMeta("name", "twitter:description", strings.metaDescription);
    for (const element of document.querySelectorAll(
      'link[rel="canonical"], link[rel="alternate"][hreflang], meta[property="og:url"], meta[name="keywords"], script[type="application/ld+json"]',
    )) {
      element.remove();
    }
  }, [strings]);

  return (
    <>
      <SiteHeader />
      <main className="content-shell empty-content">
        <h1>{strings.heading}</h1>
        <h2>{strings.title}</h2>
        <p>{strings.body}</p>
        <p>
          <a href={path("/")}>{strings.backHome}</a> · <a href={path("/#ip-check")}>{strings.ipCheck}</a> ·{" "}
          <a href={path("/blog.html")}>{strings.blog}</a>
        </p>
      </main>
      <SiteFooter />
    </>
  );
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}
